import { useEffect, useRef, useState } from 'react';
import type { BatchStatus, StatusBucket } from '@ia/shared';

/* ---------------------------------------------------------------- dates -- */

const dtf = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' });

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dtf.format(d);
}

export function relative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.round((t - now) / 1000);
  const a = Math.abs(s);
  if (a < 45) return rtf.format(s, 'second');
  if (a < 2700) return rtf.format(Math.round(s / 60), 'minute');
  if (a < 64800) return rtf.format(Math.round(s / 3600), 'hour');
  if (a < 86400 * 26) return rtf.format(Math.round(s / 86400), 'day');
  if (a < 86400 * 320) return rtf.format(Math.round(s / (86400 * 30)), 'month');
  return rtf.format(Math.round(s / (86400 * 365)), 'year');
}

export function duration(startIso: string, endIso: string | null): string {
  const ms = (endIso ? new Date(endIso).getTime() : Date.now()) - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/* -------------------------------------------------------------- buckets -- */

/** Local copy of shared `bucketOf` so zod is not bundled for one function. */
export function bucketOf(s: BatchStatus): StatusBucket {
  if (s === 'processed' || s === 'failed' || s === 'cancelled') return s;
  return 'pending';
}

export const STATUS_LABEL: Record<string, string> = {
  received: 'Received',
  previewing: 'Previewing',
  awaiting_approval: 'Awaiting approval',
  pending: 'Pending',
  processing: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
  succeeded: 'Succeeded',
  running: 'Running',
  queued: 'Queued',
};

/* ---------------------------------------------------------------- hooks -- */

export function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return visible;
}

/**
 * Calls `fn` now and then every `ms` while `enabled` and the page is visible.
 * Each tick waits for the previous one to finish (no overlapping requests).
 */
export function usePolling(fn: (signal: AbortSignal) => Promise<void>, ms: number, enabled: boolean, deps: unknown[]) {
  const visible = usePageVisible();
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    const ctrl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const tick = async () => {
      try {
        await fnRef.current(ctrl.signal);
      } catch {
        /* errors are surfaced by fn itself */
      }
      if (!stopped && enabled && visible) timer = setTimeout(tick, ms);
    };
    void tick();
    return () => {
      stopped = true;
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms, enabled, visible, ...deps]);
}

/** Re-renders every `ms` so relative timestamps stay fresh. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
